import { useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router';

// Estado de la vista en la URL (UX-NAV-02): página de la tabla y documento abierto en
// el detalle. Recargar o compartir el enlace reproduce la vista.

/** Última página pedible: el backend acepta offset hasta 2000 (páginas de 25). */
export const MAX_PAGE = Math.floor(2000 / 25) + 1;

export function parsePage(raw: string | null): number {
  if (raw === null || !/^\d{1,4}$/.test(raw)) {
    return 1;
  }
  const n = Number(raw);
  return n >= 1 ? Math.min(n, MAX_PAGE) : 1;
}

/** Id de documento válido (dígitos) o null. */
export function parseDoc(raw: string | null): string | null {
  return raw !== null && /^[1-9]\d{0,18}$/.test(raw) ? raw : null;
}

export interface TrainingUrlState {
  page: number;
  doc: string | null;
  setPage: (page: number, opts?: { replace?: boolean }) => void;
  setDoc: (entryId: string) => void;
  clearDoc: () => void;
}

export function useTrainingUrlState(): TrainingUrlState {
  const [params, setSearchParams] = useSearchParams();
  // Cambiar la query no debe perder el origen del botón de regreso (ficha → aquí).
  const state: unknown = useLocation().state;
  const setParams = useCallback(
    (fn: (prev: URLSearchParams) => URLSearchParams, opts: { replace: boolean }) =>
      setSearchParams(fn, { ...opts, state }),
    [setSearchParams, state],
  );
  const page = parsePage(params.get('page'));
  const doc = parseDoc(params.get('doc'));

  // Cambiar de página suelta el documento abierto: el detalle sigue a la nueva página.
  const setPage = useCallback(
    (next: number, opts?: { replace?: boolean }) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete('doc');
          if (next <= 1) {
            p.delete('page');
          } else {
            p.set('page', String(next));
          }
          return p;
        },
        { replace: opts?.replace ?? false },
      ),
    [setParams],
  );

  const clearDoc = useCallback(
    () =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete('doc');
          return p;
        },
        { replace: true },
      ),
    [setParams],
  );

  const setDoc = useCallback(
    (entryId: string) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('doc', entryId);
          return p;
        },
        { replace: true },
      ),
    [setParams],
  );

  return { page, doc, setPage, setDoc, clearDoc };
}
